/**
 * NearestTechBadge
 * ─────────────────────────────────────────────────────────────────────────
 * Uses navigator.geolocation (with permission) to find the closest known
 * Bagnoman coverage city and show:  "Tecnico più vicino: Empoli (~12 km)"
 *
 * Privacy-friendly:
 *  - Asks permission ONLY after an explicit tap on "Verifica la mia zona"
 *    (never on page load).
 *  - If denied / unavailable, falls back to a static, still-credible
 *    message ("Copertura completa in Toscana, Liguria, Umbria, Alto Lazio").
 *  - Coordinates never leave the browser. We just compute distance locally.
 *
 * No external dependencies. Pure React + Tailwind tokens.
 */
import React, { useEffect, useState } from "react";
import { MapPin } from "lucide-react";

// Sample of major coverage cities (lat, lng). Approximate centroids;
// the small set is enough for the "nearest city" UX trick. We only need
// to find the closest one within ~150 km.
const CITIES = [
  // Toscana
  { name: "Firenze", lat: 43.7696, lng: 11.2558 },
  { name: "Empoli", lat: 43.7172, lng: 10.9485 },
  { name: "Pisa", lat: 43.7228, lng: 10.4017 },
  { name: "Lucca", lat: 43.8429, lng: 10.5027 },
  { name: "Livorno", lat: 43.5485, lng: 10.3106 },
  { name: "Pistoia", lat: 43.9303, lng: 10.9173 },
  { name: "Prato", lat: 43.8777, lng: 11.1023 },
  { name: "Arezzo", lat: 43.4633, lng: 11.8796 },
  { name: "Siena", lat: 43.3188, lng: 11.3308 },
  { name: "Grosseto", lat: 42.7634, lng: 11.1129 },
  { name: "Massa", lat: 44.0353, lng: 10.1411 },
  { name: "Carrara", lat: 44.0793, lng: 10.0978 },
  { name: "Viareggio", lat: 43.8675, lng: 10.2406 },
  // Liguria
  { name: "La Spezia", lat: 44.1024, lng: 9.8242 },
  { name: "Genova", lat: 44.4056, lng: 8.9463 },
  { name: "Savona", lat: 44.3093, lng: 8.4779 },
  { name: "Imperia", lat: 43.8896, lng: 8.0277 },
  // Umbria
  { name: "Perugia", lat: 43.1107, lng: 12.3908 },
  { name: "Terni", lat: 42.5635, lng: 12.6438 },
  // Alto Lazio
  { name: "Viterbo", lat: 42.4178, lng: 12.1041 },
  { name: "Rieti", lat: 42.4015, lng: 12.8627 },
];

function haversineKm(a, b) {
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s1 = Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s1));
}

export function NearestTechBadge({ lang = "it", className = "", testId = "nearest-tech-badge" }) {
  const [state, setState] = useState({ status: "idle", city: null, km: null });
  // Geolocation support is a browser-only fact: start with `false` (what the
  // pre-rendered HTML was built with) and flip it after mount, so hydrateRoot
  // adopts the server markup 1:1 and the "Verifica la mia zona" button appears
  // right after hydration without a mismatch.
  const [supported, setSupported] = useState(false);
  useEffect(() => {
    if (typeof window !== "undefined" && "geolocation" in navigator) setSupported(true);
  }, []);

  // Permission is requested ONLY on user gesture (Lighthouse best practice:
  // never prompt for geolocation on page load; it is also intrusive and hurts
  // conversion on a landing page). Coordinates never leave the browser.
  const check = () => {
    if (!supported) {
      setState({ status: "fallback", city: null, km: null });
      return;
    }
    setState((p) => ({ ...p, status: "asking" }));
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const user = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        let best = null;
        let bestKm = Infinity;
        for (const c of CITIES) {
          const km = haversineKm(user, c);
          if (km < bestKm) {
            bestKm = km;
            best = c;
          }
        }
        if (best && bestKm < 200) {
          setState({ status: "ok", city: best.name, km: Math.max(2, Math.round(bestKm)) });
        } else {
          setState({ status: "fallback", city: null, km: null });
        }
      },
      () => setState({ status: "fallback", city: null, km: null }),
      { enableHighAccuracy: false, timeout: 6000, maximumAge: 60_000 * 30 },
    );
  };

  const labels = lang === "en"
    ? {
        nearest: "Your area is covered",
        fallback: "Coverage: Tuscany, Liguria, Umbria, Upper Lazio",
        asking: "Checking coverage for your area\u2026",
        km: "km from you, visit agreed by phone",
        check: "Check my area",
      }
    : {
        nearest: "La tua zona \u00e8 coperta",
        fallback: "Copertura: Toscana, Liguria, Umbria, Alto Lazio",
        asking: "Verifico la copertura della tua zona\u2026",
        km: "km da te, intervento con orario concordato",
        check: "Verifica la mia zona",
      };

  let content;
  if (state.status === "ok") {
    content = (
      <span data-testid="nearest-tech-result">
        <span className="font-semibold text-[hsl(var(--brand-navy))]">{labels.nearest}:</span>{" "}
        <span className="font-bold text-[hsl(var(--brand-aqua))]">{state.city}</span>{" "}
        <span className="text-[hsl(var(--muted-foreground))]">(~{state.km} {labels.km})</span>
      </span>
    );
  } else if (state.status === "asking") {
    content = (
      <span className="text-[hsl(var(--muted-foreground))] italic">{labels.asking}</span>
    );
  } else {
    content = (
      <span className="text-[hsl(var(--brand-navy))]">
        {labels.fallback}
        {supported && state.status === "idle" ? (
          <>
            {" \u00b7 "}
            <button
              type="button"
              onClick={check}
              className="font-semibold text-[hsl(var(--brand-aqua))] underline-offset-2 hover:underline focus-visible:underline"
              data-testid="nearest-tech-check-button"
            >
              {labels.check}
            </button>
          </>
        ) : null}
      </span>
    );
  }

  return (
    <div
      className={`inline-flex items-center gap-2 rounded-full bg-white border border-[hsl(var(--border))] px-3.5 py-2 text-sm shadow-sm max-w-full ${className}`}
      data-testid={testId}
      role="status"
      aria-live="polite"
    >
      <MapPin className="h-4 w-4 text-[hsl(var(--brand-aqua))] shrink-0" aria-hidden="true" />
      <div className="leading-tight min-w-0 break-words">{content}</div>
    </div>
  );
}

export default NearestTechBadge;
